import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useStreetBeatChat } from '@dev-streetbeat/chat/react';
import constraintsStore from 'shared/store/constraintsStore';
import { toast } from 'shared/components/ui/use-toast';
import { CONSTRAINTS } from 'pages/assistant/components/activity-panel/panels/constraints/constants';
import { buildConstraintsSyncPayload } from 'pages/assistant/components/activity-panel/panels/constraints/lifecycle/buildConstraintsSyncPayload';
import { createLogger } from 'shared/logger';

const log = createLogger('Constraints');

/**
 * Flush `constraintsStore.pendingSync` to the agent.
 *
 * Applying a profile, applying predefiniti, or Reset sets `pendingSync` to the
 * resulting Document. This hook persists it into thread metadata via the SDK's
 * `seedThread` — a metadata-only write that carries `skip_run: true`, so the
 * agent does NOT take a turn (spec §4.1). It only fires when a thread exists;
 * before the first message there is no thread, so the document instead rides
 * the first real message (handled elsewhere) and `pendingSync` is cleared there.
 */
export const useConstraintsSync = () => {
  const { seedThread } = useStreetBeatChat();
  const threadId = useParams().threadId;
  const pendingSync = constraintsStore((s) => s.pendingSync);

  useEffect(() => {
    if (!pendingSync) {
      return;
    }
    if (!threadId) {
      // `pendingSync` was set on /assistant (no thread) — typically by
      // `useFreshChatBootstrap` applying the per-mode default, or by the
      // dropdown picking a profile before the first message. It exists to
      // silent-sync a Document into an existing thread's metadata, but here
      // there is no thread to write to. If the user then clicks a history
      // entry, the URL flips to `/assistant/<existingThreadId>` and this
      // effect re-runs with the stale pendingSync — firing a seedThread
      // against an unrelated thread that's still in the middle of its WS
      // handshake. The SDK rejects, surfacing a spurious "sync failed"
      // toast even though the user did nothing.
      //
      // The first message of a fresh chat doesn't need pendingSync either:
      // `useConstraintsMetadata` rebuilds the payload from `workingDocument`
      // + `appliedProfile` at send time and ships it inline via
      // `createThreadAndStart`. So dropping pendingSync here is safe.
      constraintsStore.getState().clearPendingSync();
      return;
    }
    const flush = async () => {
      const { metadata } = buildConstraintsSyncPayload();
      // Mark the sync in flight so the UI gates Salva / Invia / Reset /
      // dropdown until the seedThread stream completes. The flag tracks the
      // promise lifecycle: set true here, cleared in `finally` regardless
      // of success or failure.
      constraintsStore.getState().setSyncInFlight(true);

      try {
        // Empty seedMessages was accepted by the SDK but the server did NOT
        // persist the standalone thread metadata — thread.connected kept
        // serving the pre-sync state, and with skip_run there is no follow-up
        // message.completed to refresh. The SDK's MessageInput has a `hidden`
        // flag exactly for this case: send a single hidden assistant message
        // so the backend persists the thread metadata alongside it; the chat
        // UI omits hidden messages from the visible transcript. The text
        // body gives the agent enough context to explain a sudden constraints
        // diff with no preceding user turn (Reset / applyProfile /
        // applyPredefiniti all fire this path).
        await seedThread(
          [{
            role: 'assistant',
            content: CONSTRAINTS.SILENT_SYNC_MESSAGE,
            hidden: true,
            metadata: { client_seeded: true },
          }],
          metadata,
        );
      } catch (error) {
        log.error('constraints silent-sync failed', { thread: threadId, error });
        // Surface the failure so the user knows their Reset / applyProfile /
        // applyPredefiniti / Salva didn't reach the server. Local state is
        // already updated (the panel reflects the user's intent), but the
        // server still holds the prior version — they need to repeat the
        // action. No payload retention here: a re-click of the same action
        // re-enqueues a fresh sync.
        toast({
          title: 'Errore di sincronizzazione',
          description:
            'Le modifiche ai vincoli non sono state salvate. Riprova l\'operazione.',
          variant: 'destructive',
        });
      } finally {
        // The SDK's `seedThread` awaits the `connected` promise (WS open),
        // NOT `completed` (DONE event). So this await resolved before the
        // `thread.updated` echo arrives; clearing syncInFlight immediately
        // un-gates the dropdown, and the trigger paints orange for a beat
        // while the change-detection hook is still settling working ↔
        // baseline against the echo. A short grace period covers the echo
        // window — the change-detection effect runs inside it, and
        // syncInFlight only flips false once the state has stabilised. The
        // grace is a hack against the SDK's early-resolve; once the SDK
        // awaits `completed` this delay can go away.
        setTimeout(() => {
          constraintsStore.getState().setSyncInFlight(false);
        }, 250);
        constraintsStore.getState().clearPendingSync();
      }
    };
    void flush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSync, threadId]);
};
